import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { buildLogSourceTree } from "./logSourceFilters";

/**
 * Hierarchical view of configured workspace log sources for the lower Log Sources panel.
 * Expand/collapse only; Edit remains on the section header.
 */
export default function LogSourceTree({
  sources,
  environmentLabel,
  platformLabel
}) {
  const tree = useMemo(() => buildLogSourceTree(sources), [sources]);
  const [openIndexes, setOpenIndexes] = useState(() => new Set());
  const [openApps, setOpenApps] = useState(() => new Set());
  const seenIndexes = useRef(new Set());

  useEffect(() => {
    setOpenIndexes((current) => {
      const next = new Set(current);
      let changed = false;
      tree.forEach((index) => {
        if (!seenIndexes.current.has(index.indexName)) {
          seenIndexes.current.add(index.indexName);
          next.add(index.indexName);
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [tree]);

  const toggleIndex = (indexName) => {
    setOpenIndexes((current) => {
      const next = new Set(current);
      if (next.has(indexName)) next.delete(indexName);
      else next.add(indexName);
      return next;
    });
  };

  const toggleApp = (key) => {
    setOpenApps((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (tree.length === 0) {
    return <div className="log-sources-empty">No sources configured yet. Click Edit to start.</div>;
  }

  return (
    <div className="log-source-tree log-sources-hierarchy" aria-label="Configured log sources">
      {tree.map((index) => {
        const indexOpen = openIndexes.has(index.indexName);
        return (
          <div key={index.indexName} className="log-source-index">
            <div className="log-source-tree-row">
              <button
                type="button"
                aria-expanded={indexOpen}
                aria-label={`${indexOpen ? "Collapse" : "Expand"} ${index.indexName}`}
                onClick={() => toggleIndex(index.indexName)}
              >
                {indexOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              <span className="log-source-tree-label log-source-tree-index">{index.indexName}</span>
            </div>
            {indexOpen && index.applications.map((app) => {
              const appKey = `${index.indexName}/${app.name}`;
              const appOpen = openApps.has(appKey);
              return (
                <div key={appKey} className="log-source-app">
                  <div className="log-source-tree-row">
                    <button
                      type="button"
                      aria-expanded={appOpen}
                      aria-label={`${appOpen ? "Collapse" : "Expand"} ${app.name}`}
                      onClick={() => toggleApp(appKey)}
                    >
                      {appOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <span className="log-source-tree-label">{app.name}</span>
                  </div>
                  {appOpen && (
                    <div className="log-source-meta">
                      {app.environments.length > 0 && (
                        <div>
                          <div className="log-source-meta-label">Environment</div>
                          <ul className="log-source-meta-list">
                            {app.environments.map((environment) => (
                              <li key={environment}>{environmentLabel(environment)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {app.platforms.length > 0 && (
                        <div>
                          <div className="log-source-meta-label">Platform</div>
                          <ul className="log-source-meta-list">
                            {app.platforms.map((platform) => (
                              <li key={platform}>{platformLabel(platform)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {app.sources.map((source, sourceIndex) => (
                        <div
                          key={source.id || `${appKey}-source-${sourceIndex}`}
                          className="log-source-tree-endpoint"
                          title={source.endpoint || undefined}
                        >
                          {source.serviceName ? `${source.serviceName} · ` : ""}
                          {source.endpoint || "No endpoint configured"}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
